import { QueryClient } from '@tanstack/react-query';

import { CertificatePermissionType, ICertificatePermission } from '../interfaces/ICertificate';

// Dedicated query key for optimistically-granted certificate permissions. It is intentionally
// NOT a prefix of ['certificate'] so that invalidating the real permissions list — which the
// certificate websocket handler does on every message — never touches it. sessionStorage is the
// durable source of truth; this cache entry is the reactive mirror that re-renders the table.
export const OPTIMISTIC_CERTIFICATE_PERMISSIONS_QUERY_KEY = ['certificate-permissions-optimistic'];

const STORAGE_KEY = 'optimisticCertificatePermissions';

// Safety net: drop a pending grant if the reconciling websocket message never arrives. It has to
// outlast a slow EDC exchange — expiring early would reveal the real row's values while the grant
// is still running, which is the misleading state the skeletons exist to prevent.
export const OPTIMISTIC_CERTIFICATE_PERMISSION_TTL_MS = 60_000;

// A pending grant. The API writes the permission row before the EDC exchange runs, so this entry is
// not simply "a row that does not exist yet": the table attaches it to the real row once that shows
// up, and only renders it as a standalone placeholder until then. It has no `id` (the POST returns
// just a message) and no review status. `certificateId` scopes it to one table, `optimisticKey`
// identifies it for removal — on rollback and when the reconciling websocket message arrives.
export type OptimisticCertificatePermission = Omit<
  ICertificatePermission,
  'id' | 'consumerReviewStatus'
> & {
  id?: undefined;
  certificateId: string;
  optimisticKey: string;
  expiresAt: number;
};

// Deterministic key shared by the optimistic row and the reconciling websocket message, which
// carries the certificate id and the receiver company BPN but no permission id. We key on the BPN
// rather than the company name because the name can come back null, whereas the BPN is the identity
// the permission is actually created against. The permission type is part of the key so that a
// `pull_certificates` message can never reconcile away a pending push, or vice versa.
export const getOptimisticCertificatePermissionKey = (
  certificateId: string | undefined,
  companyBpn: string | undefined,
  type: CertificatePermissionType,
): string => {
  const normalize = (value: string | undefined): string => {
    // Reduce to lowercase alphanumerics joined by dashes, so the key is safe to use as part of a
    // DOM id (the table feeds it into the type tooltip's selector).
    return (value ?? '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // `__` is a safe separator: normalize() never emits underscores, so it can't collide with the
  // dashes inside any part.
  return `${normalize(certificateId)}__${normalize(companyBpn)}__${normalize(type)}`;
};

const readStoredRows = (): OptimisticCertificatePermission[] => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);

    if (!stored) {
      return [];
    }

    const parsed: unknown = JSON.parse(stored);

    return Array.isArray(parsed) ? (parsed as OptimisticCertificatePermission[]) : [];
  } catch {
    // sessionStorage may be unavailable (private mode, disabled storage) or hold corrupt JSON —
    // fail open and behave as if nothing were pending.
    return [];
  }
};

// Reads the surviving pending grants. Expiry is applied here rather than by a timer alone, so that
// entries restored after a page reload — where any timer is long gone — can still expire.
export const readOptimisticCertificatePermissions = (): OptimisticCertificatePermission[] => {
  return readStoredRows().filter((row) => {
    return row?.expiresAt > Date.now();
  });
};

const writeOptimisticCertificatePermissions = (
  queryClient: QueryClient,
  rows: OptimisticCertificatePermission[],
): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
  } catch {
    // Storage is unavailable, so the pending grants only live for this page view.
  }

  queryClient.setQueryData<OptimisticCertificatePermission[]>(
    OPTIMISTIC_CERTIFICATE_PERMISSIONS_QUERY_KEY,
    rows,
  );
};

export const addOptimisticCertificatePermission = (
  queryClient: QueryClient,
  row: OptimisticCertificatePermission,
): void => {
  // The key is derived from certificate + BPN, so a second grant for the same company would
  // otherwise add a duplicate entry that renders as two identical skeleton rows sharing a row key.
  // Granting twice is prevented in the dialog; replacing here keeps the set consistent regardless.
  writeOptimisticCertificatePermissions(queryClient, [
    row,
    ...readOptimisticCertificatePermissions().filter((storedRow) => {
      return storedRow.optimisticKey !== row.optimisticKey;
    }),
  ]);
};

export const removeOptimisticCertificatePermission = (
  queryClient: QueryClient,
  optimisticKey: string,
): void => {
  writeOptimisticCertificatePermissions(
    queryClient,
    readOptimisticCertificatePermissions().filter((row) => {
      return row.optimisticKey !== optimisticKey;
    }),
  );
};

// Pending grants name the company they were granted to and the user who granted them, so they must
// not outlive the session that created them — sessionStorage is per-tab, and a second user logging
// in to the same tab would otherwise rehydrate the first user's entries.
export const clearOptimisticCertificatePermissions = (queryClient: QueryClient): void => {
  writeOptimisticCertificatePermissions(queryClient, []);
};

// Re-persists the set with expired entries dropped. It deliberately does nothing when nothing has
// expired: the caller re-arms its timer whenever this cache entry changes, so writing an unchanged
// set would spin.
export const pruneExpiredOptimisticCertificatePermissions = (queryClient: QueryClient): void => {
  const storedRows = readStoredRows();
  const survivingRows = storedRows.filter((row) => {
    return row?.expiresAt > Date.now();
  });

  if (survivingRows.length === storedRows.length) {
    return;
  }

  writeOptimisticCertificatePermissions(queryClient, survivingRows);
};
